import { useState, useRef } from "react";
import { toast } from "sonner";
import { ArrowRight, Gift, Bell, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { LEAD } from "@/constants/testIds";
import { trackEvent, attributionForSubmit } from "@/lib/analytics";

const perks = [
  { icon: Zap, text: "Exclusive member discounts" },
  { icon: Bell, text: "New product updates & drops" },
  { icon: Gift, text: "Free training tips & drills" },
];

export default function LeadCapture() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const formStarted = useRef(false);

  const update = (k) => (e) => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent("contact_form_started", { form_name: "team-pickle-wall-signup" });
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "team-pickle-wall", attribution: attributionForSubmit() });
      trackEvent("newsletter_signup", { form_name: "team-pickle-wall-signup" });
      toast.success("Welcome to Team Pickle Wall™ — check your email soon.");
      setForm({ name: "", email: "", phone: "" });
    } catch (err) {
      toast.error("Couldn't subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="pre-order"
      className="relative py-24 md:py-36 bg-[#0a0a0a] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#FF6B00] opacity-[0.08] rounded-full blur-[160px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-white/10 rounded-3xl p-8 md:p-16 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 text-[280px] font-display font-black text-[#FF6B00]/[0.04] leading-none pointer-events-none">
            PRE
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00] mb-4">
                Join The Movement
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-balance leading-[0.95]">
                Join Team <br />
                <span className="text-[#FF6B00]">Pickle Wall™</span>
              </h2>
              <p className="mt-6 text-lg text-white/70 max-w-md">
                Be first to know. Get early access, exclusive drops, and pro
                training tips delivered weekly.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-3">
                {perks.map((p) => (
                  <div
                    key={p.text}
                    className="flex items-center gap-3 text-sm text-white/80"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center">
                      <p.icon size={16} className="text-[#FF6B00]" />
                    </div>
                    {p.text}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submit} data-testid={LEAD.form} className="space-y-4">
              <Field
                testId={LEAD.name}
                label="Full Name"
                value={form.name}
                onChange={update("name")}
                required
              />
              <Field
                testId={LEAD.email}
                label="Email Address"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
              />
              <Field
                testId={LEAD.phone}
                label="Phone (optional)"
                value={form.phone}
                onChange={update("phone")}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid={LEAD.submit}
                className="w-full group bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 inline-flex items-center justify-center gap-2 transition-all hover:orange-glow"
              >
                {loading ? "Joining..." : "Join Team Pickle Wall™"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-xs text-white/40 text-center">
                No spam. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ testId, label, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50 mb-2 block">
        {label}
      </span>
      <input
        data-testid={testId}
        {...rest}
        className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF6B00] outline-none rounded-xl px-4 py-4 text-white placeholder-white/30 text-base transition-colors"
      />
    </label>
  );
}
